import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { 
  Bookmark, 
  Sparkles, 
  Quote, 
  Heart, 
  Trash2,
  Loader2,
  ArrowLeft,
  Share2,
  Facebook,
  Instagram,
  Linkedin,
  Mail,
  MessageSquare,
  Copy,
  Check,
  Download,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  FacebookShareButton,
  LinkedinShareButton,
  WhatsappShareButton,
  EmailShareButton
} from 'react-share';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FavoritesSoulFoodPage() {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedSoulFood, setSelectedSoulFood] = useState(null);
  const [shareData, setShareData] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [downloadingImage, setDownloadingImage] = useState(false);

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/soul-food/favorites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(response.data.favorites || []);
    } catch (error) {
      console.error('Error fetching favorites:', error);
      toast.error('Failed to load favorites');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFavorite = async (soulFoodId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/soul-food/${soulFoodId}/favorite`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Remove from local state
      setFavorites(prev => prev.filter(f => f.id !== soulFoodId));
      toast.success('Removed from favorites');
    } catch (error) {
      toast.error('Failed to remove from favorites');
    }
  };

  const handleOpenShare = async (soulFood) => {
    setSelectedSoulFood(soulFood);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/soul-food/${soulFood.id}/share-data`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShareData(response.data);
      setShowShareModal(true);
    } catch (error) {
      toast.error('Failed to load share options');
    }
  };

  const handleCopyLink = async () => {
    if (!shareData) return;
    try {
      await navigator.clipboard.writeText(shareData.share_url);
      setCopiedLink(true);
      toast.success('Link copied to clipboard!');
      trackShare('copy_link');
      setTimeout(() => setCopiedLink(false), 3000);
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const handleDownloadImage = async () => {
    if (!selectedSoulFood) return;
    setDownloadingImage(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/soul-food/${selectedSoulFood.id}/share-image`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `soul-food-${selectedSoulFood.id}.png`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Image downloaded!');
      trackShare('download_image');
    } catch (error) {
      toast.error('Failed to download image');
    } finally {
      setDownloadingImage(false);
    }
  };

  const trackShare = async (platform) => {
    if (!selectedSoulFood) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/soul-food/${selectedSoulFood.id}/track-share`,
        { platform },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Failed to track share:', error);
    }
  };

  const getShareText = () => {
    if (!selectedSoulFood) return '';
    return `"${selectedSoulFood.quote_or_scripture}"\n— ${selectedSoulFood.source}\n\n#SisterDiamonds #${selectedSoulFood.theme?.replace(/\s+/g, '') || 'Inspiration'}`;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-heading text-forest flex items-center gap-2">
                <Bookmark className="h-6 w-6 text-amber-500" />
                My Saved Soul Food
              </h1>
              <p className="text-gray-500">Your collection of favorite inspirational messages</p>
            </div>
          </div>
          <Badge variant="secondary" className="text-lg px-4 py-1">
            {favorites.length} saved
          </Badge>
        </div>

        {/* Favorites List */}
        {favorites.length === 0 ? (
          <Card className="border-2 border-dashed border-gray-200">
            <CardContent className="py-16 text-center">
              <Bookmark className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-xl font-medium text-gray-600 mb-2">No favorites yet</h3>
              <p className="text-gray-500 mb-4">
                Start saving Soul Food messages that inspire you! <br />
                Click the bookmark icon on any Soul Food to save it here.
              </p>
              <Link to="/dashboard">
                <Button>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Go to Dashboard
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {favorites.map((soulFood) => (
              <Card 
                key={soulFood.id} 
                className="border-2 border-purple-100 bg-gradient-to-br from-purple-50 to-pink-50 hover:shadow-md transition-shadow"
                data-testid={`favorite-soul-food-${soulFood.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center">
                        <Sparkles className="h-4 w-4 text-purple-600" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{soulFood.title || 'Soul Food'}</CardTitle>
                        <CardDescription>Week of {soulFood.week_of}</CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {soulFood.theme && (
                        <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                          {soulFood.theme}
                        </Badge>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-gray-400 hover:text-red-500"
                        onClick={() => handleRemoveFavorite(soulFood.id)}
                        data-testid={`remove-favorite-${soulFood.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent>
                  {/* Quote/Scripture */}
                  <div className="relative my-4 px-4">
                    <Quote className="absolute -top-2 -left-1 h-6 w-6 text-purple-200" />
                    <p className="text-lg italic text-gray-800 leading-relaxed pl-4">
                      "{soulFood.quote_or_scripture}"
                    </p>
                    <p className="text-sm text-purple-600 font-medium mt-2 pl-4">
                      — {soulFood.source}
                    </p>
                  </div>
                  
                  {/* Reflection */}
                  <p className="text-gray-700 leading-relaxed">
                    {soulFood.reflection}
                  </p>
                  
                  {/* Stats */}
                  <div className="flex items-center gap-4 mt-4 pt-3 border-t border-purple-100 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Heart className="h-4 w-4" />
                      {soulFood.likes?.length || 0} likes
                    </span>
                    {soulFood.is_scripture && (
                      <Badge variant="outline" className="text-xs">Scripture</Badge>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-auto text-purple-500 hover:text-purple-700 hover:bg-purple-50 gap-1"
                      onClick={() => handleOpenShare(soulFood)}
                      data-testid={`share-favorite-${soulFood.id}`}
                    >
                      <Share2 className="h-4 w-4" />
                      Share
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Share Modal */}
        <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
          <DialogContent className="sm:max-w-md" data-testid="share-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Share2 className="h-5 w-5 text-purple-600" />
                Share Soul Food
              </DialogTitle>
              <DialogDescription>
                Spread the inspiration with your community
              </DialogDescription>
            </DialogHeader>

            {selectedSoulFood && (
              <div className="space-y-6">
                {/* Preview Card */}
                <div className="p-4 rounded-lg bg-gradient-to-br from-purple-100 to-pink-100 border border-purple-200">
                  <p className="text-sm italic text-gray-800 line-clamp-3">
                    "{selectedSoulFood.quote_or_scripture}"
                  </p>
                  <p className="text-xs text-purple-600 mt-1">— {selectedSoulFood.source}</p>
                </div>

                {/* Social Share Buttons */}
                <div className="space-y-3">
                  <p className="text-sm font-medium text-gray-700">Share on Social Media</p>
                  <div className="grid grid-cols-4 gap-3">
                    <FacebookShareButton
                      url={shareData?.share_url || ''}
                      quote={getShareText()}
                      onClick={() => trackShare('facebook')}
                      className="w-full"
                    >
                      <div className="flex flex-col items-center gap-1 p-3 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors cursor-pointer">
                        <Facebook className="h-6 w-6 text-blue-600" />
                        <span className="text-xs text-blue-700">Facebook</span>
                      </div>
                    </FacebookShareButton>

                    <a
                      href="https://www.instagram.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackShare('instagram')}
                      className="w-full"
                      title="Share on Instagram (download image first)"
                    >
                      <div className="flex flex-col items-center gap-1 p-3 rounded-lg bg-gradient-to-br from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 transition-colors cursor-pointer">
                        <Instagram className="h-6 w-6 text-pink-600" />
                        <span className="text-xs text-pink-700">Instagram</span>
                      </div>
                    </a>

                    <LinkedinShareButton
                      url={shareData?.share_url || ''}
                      title="Soul Food from Sister Diamonds"
                      summary={getShareText()}
                      onClick={() => trackShare('linkedin')}
                      className="w-full"
                    >
                      <div className="flex flex-col items-center gap-1 p-3 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors cursor-pointer">
                        <Linkedin className="h-6 w-6 text-blue-700" />
                        <span className="text-xs text-blue-800">LinkedIn</span>
                      </div>
                    </LinkedinShareButton>

                    <WhatsappShareButton
                      url={shareData?.share_url || ''}
                      title={getShareText()}
                      onClick={() => trackShare('whatsapp')}
                      className="w-full"
                    >
                      <div className="flex flex-col items-center gap-1 p-3 rounded-lg bg-green-50 hover:bg-green-100 transition-colors cursor-pointer">
                        <MessageSquare className="h-6 w-6 text-green-600" />
                        <span className="text-xs text-green-700">WhatsApp</span>
                      </div>
                    </WhatsappShareButton>
                  </div>

                  {/* Second row */}
                  <div className="grid grid-cols-4 gap-3">
                    <EmailShareButton
                      url={shareData?.share_url || ''}
                      subject="Soul Food from Sister Diamonds"
                      body={getShareText()}
                      onClick={() => trackShare('email')}
                      className="w-full"
                    >
                      <div className="flex flex-col items-center gap-1 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer">
                        <Mail className="h-6 w-6 text-gray-600" />
                        <span className="text-xs text-gray-700">Email</span>
                      </div>
                    </EmailShareButton>

                    <button
                      onClick={handleCopyLink}
                      className="w-full"
                    >
                      <div className="flex flex-col items-center gap-1 p-3 rounded-lg bg-purple-50 hover:bg-purple-100 transition-colors cursor-pointer">
                        {copiedLink ? (
                          <Check className="h-6 w-6 text-green-600" />
                        ) : (
                          <Copy className="h-6 w-6 text-purple-600" />
                        )}
                        <span className="text-xs text-purple-700">
                          {copiedLink ? 'Copied!' : 'Copy Link'}
                        </span>
                      </div>
                    </button>

                    <button
                      onClick={handleDownloadImage}
                      disabled={downloadingImage}
                      className="w-full"
                    >
                      <div className="flex flex-col items-center gap-1 p-3 rounded-lg bg-pink-50 hover:bg-pink-100 transition-colors cursor-pointer">
                        {downloadingImage ? (
                          <Loader2 className="h-6 w-6 text-pink-600 animate-spin" />
                        ) : (
                          <Download className="h-6 w-6 text-pink-600" />
                        )}
                        <span className="text-xs text-pink-700">Image</span>
                      </div>
                    </button>

                    <a
                      href={shareData?.share_url || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackShare('direct_link')}
                      className="w-full"
                    >
                      <div className="flex flex-col items-center gap-1 p-3 rounded-lg bg-amber-50 hover:bg-amber-100 transition-colors cursor-pointer">
                        <ExternalLink className="h-6 w-6 text-amber-600" />
                        <span className="text-xs text-amber-700">Open</span>
                      </div>
                    </a>
                  </div>
                </div>

                {/* Branding Note */}
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
                  <p className="text-xs text-purple-700 text-center">
                    When you share, you're helping spread the Sister Diamonds mission to empower extraordinary women everywhere.
                  </p>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
